import { useEffect, useRef } from 'react';

import { Box, Paragraph } from '@zillow/constellation-10';

import PropertyConstants from '../../../../../constants/PropertyConstants';

import ListingActivationTerms from '../../../../ListingActivationTerms';
import StaticMap from '../../../../StaticMap';

import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

const {
  PROPERTY_TYPES: { COMMUNITY, MULTIPLEX },
} = PropertyConstants;

export const renderAddressConfirmationBody = (propertyType: string) => {
  switch (propertyType) {
    case COMMUNITY: {
      return (
        <>
          The address should <strong>exactly</strong> match the USPS-validated address for the property. Once you
          confirm, you can&apos;t edit the address.
        </>
      );
    }
    case MULTIPLEX: {
      return (
        <>
          Confirm this is the USPS street address for your property. The street address itself can&apos;t be changed
          once confirmed.
        </>
      );
    }
    default: {
      return (
        <>
          The address should <strong>exactly</strong> match the USPS-validated address for the property. Once you
          confirm, you can&apos;t edit the address, including adding, changing, or removing the unit number.
        </>
      );
    }
  }
};

type Props = {
  address: string;
  isRoomForRent: boolean;
  numUnits?: string;
  propertyType: string;
  unit?: string;
  shouldShowStaticMap?: boolean;
  showImpreciseLocationAlert?: boolean;
  showTermsInComponent?: boolean;
  user?: RentalManagerAuthCheckUserInfoWebApiDto;
  embedded?: boolean;
};

const ConfirmAddress = ({
  address,
  isRoomForRent,
  numUnits,
  propertyType,
  unit = '',
  shouldShowStaticMap = true,
  showImpreciseLocationAlert = false,
  showTermsInComponent = false,
  user,
  embedded = false,
}: Props) => {
  const topMostElementRef = useRef<HTMLParagraphElement>(null);

  const roomForRentDisplay = 'Room for rent';

  const isMultiplexType = propertyType === MULTIPLEX;

  useEffect(() => {
    topMostElementRef?.current?.scrollIntoView();
  }, []);

  const renderUnitNumber = () => {
    const hasUnitOrNumUnits = unit || (numUnits && Number(numUnits) > 0);

    if (!hasUnitOrNumUnits) {
      return null;
    }

    switch (propertyType) {
      case COMMUNITY: {
        return (
          <>
            <Paragraph data-testid="number-of-units-label" css={{ paddingTop: '600' }}>
              <strong>Number of units</strong>
            </Paragraph>
            <Paragraph data-testid="number-of-units-value">{numUnits}</Paragraph>
          </>
        );
      }

      case MULTIPLEX: {
        return (
          <>
            <Paragraph data-testid="number-of-units-label" css={{ paddingTop: '600' }}>
              <strong>Number of units you own or manage</strong>
            </Paragraph>
            <Paragraph data-testid="number-of-units-value">{numUnits}</Paragraph>
          </>
        );
      }

      default: {
        return (
          <>
            <Paragraph data-testid="unit-number-label" css={{ paddingTop: '600' }}>
              <strong>Unit number</strong>
            </Paragraph>
            <Paragraph data-testid="unit-number-value">{unit}</Paragraph>
          </>
        );
      }
    }
  };

  return (
    <>
      {!embedded && (
        <Paragraph
          data-testid="body-content"
          ref={topMostElementRef}
          css={{ paddingBottom: { base: '800', md: '1000' }, paddingRight: '800' }}
        >
          {renderAddressConfirmationBody(propertyType)}
        </Paragraph>
      )}

      <Box
        data-testid="listing-details-container"
        css={{
          display: 'flex',
          paddingBottom: '200',
          paddingRight: { md: '800' },
          flexDirection: { base: 'column', lg: embedded ? 'column' : 'row' },
          alignItems: 'flex-start',
          gap: { base: '200', md: '800' },
        }}
      >
        <Box
          css={{
            paddingRight: 'default',
            flex: { lg: embedded ? 'none' : '0 0 40%' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: embedded ? '100%' : 'auto',
          }}
        >
          <Box>
            <Paragraph>
              <strong>Street address</strong>
            </Paragraph>
            <Paragraph>{address}</Paragraph>

            {isMultiplexType ? (
              <>
                <Paragraph css={{ paddingTop: '600' }}>
                  <strong>Property type</strong>
                </Paragraph>

                <Paragraph>{propertyType}</Paragraph>

                {renderUnitNumber()}
              </>
            ) : (
              <>
                {renderUnitNumber()}

                <Paragraph css={{ paddingTop: '600' }}>
                  <strong>Property type</strong>
                </Paragraph>

                <Paragraph>{propertyType}</Paragraph>
              </>
            )}

            {isRoomForRent && <Paragraph>{roomForRentDisplay}</Paragraph>}

            {isMultiplexType && (
              <Paragraph css={{ paddingTop: '600' }}>
                <strong>Note:</strong>&nbsp;You can add or update unit numbers in the next step.
              </Paragraph>
            )}
          </Box>
        </Box>

        {shouldShowStaticMap && (
          <Box css={{ borderRadius: '300', border: '1px solid', borderColor: 'border.subtle', overflow: 'hidden' }}>
            <StaticMap
              address={address}
              marginTop={{ md_lte: 'sm' }}
              showImpreciseLocationAlert={showImpreciseLocationAlert}
              isMultiplexType={isMultiplexType}
              user={user}
            />
          </Box>
        )}
      </Box>

      {showTermsInComponent && (
        <>
          {embedded ? (
            <ListingActivationTerms actionText="By continuing" />
          ) : (
            <Box css={{ paddingTop: '800', width: '100%' }}>
              <ListingActivationTerms actionText="By continuing" />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default ConfirmAddress;
